import { Trans } from "react-i18next";
import { CommonButton } from "@components/CommonButton";
import { ProductEditorBody } from "./ProductEditorBody";
import type { ProductFormState, ProductImageFile, SellingPointItem } from "./types";
import type { SellingPointsStatus } from "./useProductForm";

export interface ProductEditorProps {
  form: ProductFormState;
  imageFiles: ProductImageFile[];
  submitting: boolean;
  sellingPointsStatus: SellingPointsStatus;
  aiAnalyzing: boolean;
  needAiAnalyze: boolean;
  isEditMode: boolean;
  showRefresh: boolean;
  onChangeForm: (patch: Partial<ProductFormState>) => void;
  onFilesChange: (files: ProductImageFile[]) => void;
  uploadFile: (file: File, ctx: any) => Promise<any>;
  onChangeCaption: (uid: string, caption: string) => void;
  onChangeSellingPoints: (items: SellingPointItem[]) => void;
  onAddSellingPoint: (content: string) => void;
  onRemoveSellingPoint: (id: string) => void;
  onRefreshSellingPoints?: () => void;
  onAiAnalyze: () => void;
  onCancel: () => void;
  onSubmit: () => void;
}

/**
 * Create Product Entity Modal 的主编辑表单（含 Banner 提示 + ProductEditorBody + Footer）。
 *
 * 设计稿节点：3433:23458（720x684 modal）。
 *
 * 结构：
 * 1. Banner（-mx-[16px] 拉满，bg-kds-bg-subtle）
 * 2. ProductEditorBody — 图片 + Name + Category + SellingPoints
 * 3. 底部 Cancel + (AI Analyze | Save) — 按 needAiAnalyze 切换
 */
export const ProductEditor = ({
  form,
  imageFiles,
  submitting,
  sellingPointsStatus,
  aiAnalyzing,
  needAiAnalyze,
  isEditMode,
  showRefresh,
  onChangeForm,
  onFilesChange,
  uploadFile,
  onChangeCaption,
  onChangeSellingPoints,
  onAddSellingPoint,
  onRemoveSellingPoint,
  onRefreshSellingPoints,
  onAiAnalyze,
  onCancel,
  onSubmit,
}: ProductEditorProps) => {
  const hasMainImage = imageFiles.length > 0 && !!imageFiles[0]?.url;
  const isComplete = hasMainImage && !!form.title.trim() && !!form.category.trim();

  return (
    <div className="flex flex-col">
      <div className="-mx-[16px] -mt-[8px] mb-[16px] bg-kds-bg-subtle px-[16px] pt-[12px] pb-[10px]">
        <p className="m-0 text-[14px] leading-[1.4] text-kds-fg-soft">
          <Trans>
            Upload multi-angle photos of a single SKU (style/color). Avoid mixing different variants, and exclude images
            with excessive text, accessories (e.g., replacement brush heads), or group shots (e.g., product sets).
          </Trans>
        </p>
      </div>

      <ProductEditorBody
        form={form}
        imageFiles={imageFiles}
        sellingPointsStatus={sellingPointsStatus}
        showRefreshSellingPoints={showRefresh}
        variant="modal"
        onChangeForm={onChangeForm}
        onFilesChange={onFilesChange}
        uploadFile={uploadFile}
        onChangeCaption={onChangeCaption}
        onChangeSellingPoints={onChangeSellingPoints}
        onAddSellingPoint={onAddSellingPoint}
        onRemoveSellingPoint={onRemoveSellingPoint}
        onRefreshSellingPoints={onRefreshSellingPoints}
      />

      <div className="-mx-[16px] -mb-[16px] flex items-center justify-end gap-[12px] border-t border-kds-border px-[16px] py-[16px]">
        <CommonButton onClick={onCancel} disabled={submitting || aiAnalyzing}>
          <Trans>Cancel</Trans>
        </CommonButton>
        {needAiAnalyze && !isEditMode ? (
          <CommonButton
            type="primary"
            loading={aiAnalyzing}
            disabled={!hasMainImage || !form.title.trim() || !form.category.trim()}
            onClick={onAiAnalyze}
          >
            <Trans>AI Analyze</Trans>
          </CommonButton>
        ) : (
          <CommonButton type="primary" loading={submitting} disabled={!isComplete} onClick={onSubmit}>
            <Trans>Save</Trans>
          </CommonButton>
        )}
      </div>
    </div>
  );
};
